import {
  LOG_IN,
  LOG_OUT,
  SET_API_ENDPOINT,
  SET_CONFIG,
  SET_LANGUAGE,
  UPDATE_LAST_ACTIVITY,
  START_SESSION,
  SET_ENDPOINT_CORRECT,
  SET_DASHBOARDS,
  SET_DASHBOARD,
  SAVE_REPORTS,
  SET_REPORT_FILTER_VALUES,
  SET_GLOBAL_FILTER_VALUES,
  SET_ENDPOINT_PAGE_STEP,
  SET_ENDPOINT_PAGE_MANUAL_ENTERING,
  SET_SESSION_ERROR_PENDING,
  SET_ENDPOINT_ALREADY_ENTERED,
  SET_AUTOLOGOUT_PENDING,
  SET_BRANCH_NAME,
  SET_VENUE_CONTEXT,
  SET_MENU_ITEMS,
  SET_MENU_ID,
  SET_DEFAULT_CURRENCY_FORMAT,
  SET_DEFAULT_NUMBER_FORMAT,
  SET_FORM_TOKENS,
} from '../../../constants';
import { getDateString } from '../../../utils/filters/getDateString';
import { getDefaultFilterData } from '../../../utils/filters/getDefaultFilterData';
import { getRangeForTimespan } from '../../../utils/filters/getRangeForTimespan';
import { UserState, UserStateAction } from '../../../types/context';

export const userStateReducer = (
  state: UserState,
  action: UserStateAction
): UserState => {
  const normalizeVenueName = (value: string | null | undefined) =>
    value?.trim() || null;

  switch (action.type) {
    case LOG_IN:
      const globalFilterValues = { ...state.globalFilterValues };
      if (!globalFilterValues[action.userLogin]) {
        globalFilterValues[action.userLogin] = [];
      }
      const reportFilterValues = { ...state.filterValues };
      if (!reportFilterValues[action.userLogin]) {
        reportFilterValues[action.userLogin] = {};
      }
      return {
        ...state,
        token: action.sessionToken,
        username: action.username,
        userLogin: action.userLogin,
        keepLoggedIn: action.keepLoggedIn,
        lastActivityTime: Number(new Date()),
        loggedInAt: Number(new Date()),
        globalFilterValues,
        filterValues: reportFilterValues,
        venueName: normalizeVenueName(action.venueName),
        venueNameStatus: normalizeVenueName(action.venueName)
          ? 'ready'
          : 'unavailable',
      };
    case LOG_OUT:
      return {
        ...state,
        username: null,
        userLogin: null,
        lastActivityTime: 0,
        loggedInAt: 0,
        keepLoggedIn: false,
        autologoutPending: false,
        menuItems: [],
        defaultCurrencyFormat: null,
        venueName: null,
        venueNameStatus: 'loading',
        defaultNumberFormat: null,
      };
    case SET_LANGUAGE:
      return { ...state, language: action.language };
    case START_SESSION:
      return {
        ...state,
        token: action.payload.sessionToken,
        venueName: null,
        venueNameStatus: 'loading',
      };
    case SET_CONFIG:
      return {
        ...state,
        usePassword: action.payload.usePassword,
        useOtp: action.payload.useOtp,
        sessionErrorPending: false,
      };
    case SET_API_ENDPOINT:
      return {
        ...state,
        apiEndpoint: action.apiEndpoint,
        branchName: action.branchName,
        username: null,
        venueName: null,
        venueNameStatus: 'loading',
      };
    case SET_ENDPOINT_CORRECT:
      return {
        ...state,
        endpointCorrect: action.payload,
      };
    case SET_ENDPOINT_PAGE_STEP:
      return {
        ...state,
        endpointPageStep: action.payload,
      };
    case SET_ENDPOINT_ALREADY_ENTERED:
      return {
        ...state,
        endpointAlreadyEntered: action.payload,
      };
    case SET_ENDPOINT_PAGE_MANUAL_ENTERING:
      return {
        ...state,
        endpointPageManualEntering: action.payload,
      };
    case UPDATE_LAST_ACTIVITY:
      return {
        ...state,
        lastActivityTime: Number(new Date()),
      };
    case SET_DASHBOARDS:
      return {
        ...state,
        dashboards: action.payload,
      };
    case SET_DASHBOARD:
      const newDashboars = [...state.dashboards];
      const index = newDashboars.findIndex(({ id }) => id === action.id);
      if (index === -1) {
        return { ...state };
      }
      newDashboars[index] = action.data;
      return {
        ...state,
        dashboards: newDashboars,
      };

    case SAVE_REPORTS:
      const reports = [...action.payload];
      const filterValues = { ...state.filterValues };
      const userLogin = state.userLogin || '';
      if (!filterValues[userLogin]) {
        filterValues[userLogin] = {};
      }
      reports.forEach(({ id, filters }) => {
        if (!filterValues[userLogin][id]) {
          filterValues[userLogin][id] = [];
        }

        filters?.forEach((filter: any) => {
          const index = filterValues[userLogin][id].findIndex(
            ({ id }) => id === filter.id
          );
          if (index === -1) {
            const defaultFilterData = getDefaultFilterData(filter);
            if (defaultFilterData) {
              filterValues[userLogin][id].push(defaultFilterData);
            }
          } else if (
            ['DATE', 'DATERANGE'].includes(filter.type) &&
            filterValues[userLogin][id][index].namedRange
          ) {
            const range = getRangeForTimespan(
              filterValues[userLogin][id][index].namedRange
            );
            let values;
            if (filter.type === 'DATERANGE' && range.from && range.to) {
              values = [getDateString(range.from), getDateString(range.to)];
            } else if (filter.type === 'DATE' && range.from) {
              values = [getDateString(range.from)];
            }
            if (values) {
              filterValues[userLogin][id][index].values = values;
            }
          } else if (
            ['SELECT', 'MULTISELECT'].includes(filter.type) &&
            filterValues[userLogin][id][index].values
          ) {
            // Remove values that are no longer in availableValues
            let values = filterValues[userLogin][id][index].values as any;
            values = values.filter((value) => {
              const foundAvailableValue = filter.availableValues.find(
                (availableValue) => availableValue.key === value
              );
              return !!foundAvailableValue;
            });
            filterValues[userLogin][id][index].values = values;
          }
        });
        filterValues[userLogin][id] = filterValues[userLogin][id].filter(
          ({ id }) => {
            const filter = filters?.find((filter) => filter.id === id);
            if (
              filter &&
              ['SELECT', 'MULTISELECT'].includes(filter.type) &&
              // @ts-expect-error throws availableValues typescript error
              !filter.availableValues?.length
            ) {
              return false;
            }
            return !!filter;
          }
        );
      });
      return {
        ...state,
        reports,
        filterValues,
      };
    case SET_REPORT_FILTER_VALUES:
      const newFilterValues = { ...state.filterValues };
      newFilterValues[state.userLogin || ''][action.id] = action.filterData;
      return {
        ...state,
        filterValues: newFilterValues,
      };
    case SET_GLOBAL_FILTER_VALUES:
      const newGlobalFilterValues = state.globalFilterValues;
      newGlobalFilterValues[state.userLogin || ''] = action.filterData;
      return {
        ...state,
        globalFilterValues: newGlobalFilterValues,
      };
    case SET_SESSION_ERROR_PENDING:
      return {
        ...state,
        sessionErrorPending: action.payload,
      };
    case SET_AUTOLOGOUT_PENDING:
      return {
        ...state,
        autologoutPending: action.payload,
      };
    case SET_BRANCH_NAME:
      return {
        ...state,
        branchName: action.payload,
      };
    case SET_VENUE_CONTEXT: {
      if (action.sessionToken !== state.token || state.username === null) {
        return state;
      }
      if (action.payload === undefined) {
        return {
          ...state,
          venueName: null,
          venueNameStatus: 'loading',
        };
      }
      const venueName = normalizeVenueName(action.payload);
      return {
        ...state,
        venueName,
        venueNameStatus: venueName ? 'ready' : 'unavailable',
      };
    }
    case SET_MENU_ITEMS:
      return {
        ...state,
        menuItems: action.payload,
      };
    case SET_MENU_ID:
      return {
        ...state,
        menuId: action.payload,
      };
    case SET_DEFAULT_CURRENCY_FORMAT:
      return {
        ...state,
        defaultCurrencyFormat: action.payload,
      };
    case SET_DEFAULT_NUMBER_FORMAT:
      return {
        ...state,
        defaultNumberFormat: action.payload,
      };
    case SET_FORM_TOKENS:
      return {
        ...state,
        formTokens: action.payload,
      };
    default:
      return state;
  }
};
